import React from "react";
import {
  FormControl,
  FormHelperText,
  Grid,
  Input,
  TextField,
  Typography,
  withStyles
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import {
  ConsultantSelectorSearchCardFragment,
  ConsultantSelector_Search as search,
  ConsultantsQueryInput
} from "@graphql";
import { Classes, ThemeType } from "@theme";
import { renderAOESelector } from "@search/filters";
import { ConsultantCardContent } from "@consultants/ConsultantCard/ConsultantCardContent";
import { Props, initialFilter, SearchFilter } from "./utils";
import ConsultantSelectorInput, {
  ConsultantSelectorInputProps
} from "./ConsultantSelectorInput";
import { Value } from "../base/utils";
import { VirtualSelectorStore } from "../base/VirtualSelectorStore";
import VirtualSelector from "../base/VirtualSelector";
import { SlimVirtualSelectorValue } from "../base/SlimVirtualSelectorValue";

export type ConsultantSelectorValue = ConsultantSelectorSearchCardFragment;

export class ConsultantSelector extends React.Component<
  Props & { classes: Classes<typeof styles> }
> {
  static defaultProps: Partial<Props> = {
    clearable: true,
    disabledFilterFields: [],
    label: locale.SelectSomething(locale.Consultants),
    initialFilter: {
      areasOfExpertise: [],
      text: "",
      sort: "name_asc"
    }
  };

  store: VirtualSelectorStore<SearchFilter, ConsultantSelectorValue, Value>;

  constructor(props) {
    super(props);

    const storeProps = {
      multi: false,
      initialFilter,
      fetch: () =>
        search
          .func(
            {
              filter: this.getQueryFilter()
            },
            { fetchPolicy: "network-only" }
          )
          .then((result) => result.data.consultantsFast.items),

      fetchMore: ({ limit, skip }) =>
        search
          .func(
            {
              filter: this.getQueryFilter(),
              skip,
              limit
            },
            { fetchPolicy: "network-only" }
          )
          .then((x) => x.data.consultantsFast.items),
      onChange: this.props.onChange
    };

    if (props.multi) {
      this.store = new VirtualSelectorStore<
        SearchFilter,
        ConsultantSelectorValue,
        Value[]
      >({
        ...storeProps,
        multi: true,
        onChange: props.onChange,
        shouldFetch: (f) => f.text.length >= 2
      });
    } else if (!props.multi) {
      this.store = new VirtualSelectorStore<SearchFilter, ConsultantSelectorValue, Value>(
        {
          ...storeProps,
          multi: false,
          onChange: props.onChange,
          shouldFetch: (f) => f.text.length >= 2
        }
      );
    }
    (window as any).consultantSelectorStore = this.store;
    this.store.setFilter(props.initialFilter);
  }

  open = (_initialFilter?: SearchFilter) => {
    if (this.store.isSearchOpen) {
      return;
    }

    this.store.openSearchDialog(_initialFilter || initialFilter);
  };

  setFilterText(v: string) {
    this.store.setFilter({ text: v, sort: v ? "relevance" : "name_asc" });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.initialFilter !== prevProps.initialFilter) {
      this.store.setFilter(this.props.initialFilter);
    }
  }

  render() {
    return (
      <VirtualSelector<ConsultantSelectorValue>
        store={this.store}
        renderControl={this.renderControl}
        renderItem={(data, selected) => (
          <ConsultantCardContent
            details={[
              "availability",
              "email",
              "phone",
              "lastContact",
              "employeeSalaryClaim",
              "subcontractorSalaryClaim",
              "prescriptionCode"
            ]}
            item={data}
            onClick={() => this.store.selectItem(data)}
            selected={selected}
          />
        )}
        renderFilter={this.renderFilter}
        waitingContent={
          <Typography variant="body1" style={{ fontWeight: 500, marginTop: 40 }}>
            Vänligen skriv något i sökfältet för att se resultat.
          </Typography>
        }
      />
    );
  }

  private getQueryFilter = (): ConsultantsQueryInput => {
    const filter = this.store ? this.store.filter : this.props.initialFilter;

    return {
      areasOfExpertise: filter.areasOfExpertise.map((x) => ({
        name: x,
        exclusive: false
      })),
      text: filter.text
    };
  };

  private handleInputClick = (e: React.SyntheticEvent<any>) => {
    this.open(this.props.initialFilter);
  };

  private renderControl = () => {
    const { classes, invisible, value, multi, slim, control } = this.props;

    if (invisible) {
      return null;
    }

    const inputProps: ConsultantSelectorInputProps = {
      clearable: this.props.clearable,
      disabled: this.props.disabled,
      onClear: this.store.clear,
      onClick: this.props.disabled ? undefined : this.handleInputClick
    };

    if (control) {
      return <div onClick={this.handleInputClick}>{control}</div>;
    }

    const label = this.props.error || this.props.label || "";

    const somethingSelected = Array.isArray(value) ? value.length > 0 : Boolean(value);

    return (
      <FormControl fullWidth={this.props.fullWidth}>
        <TextField
          label={label}
          disabled={this.props.disabled}
          required={this.props.required}
          InputLabelProps={{
            shrink: somethingSelected || slim,
            error: Boolean(this.props.error)
          }}
          classes={{
            root: classes.inputRoot
          }}
          InputProps={{
            inputComponent: slim ? SlimVirtualSelectorValue : ConsultantSelectorInput
          }}
          error={Boolean(this.props.error)}
          inputProps={inputProps}
          name="ConsultantSelector"
          value={multi ? value : [value].filter(Boolean)}
        />

        {this.props.helperText && (
          <FormHelperText>{locale.OpenConsultantSelector}</FormHelperText>
        )}
      </FormControl>
    );
  };

  private renderFilter = () => {
    const aoe = renderAOESelector(
      (v) =>
        this.store.setFilter({
          areasOfExpertise: v
        }),
      this.store.filter.areasOfExpertise,
      this.props.disabledFilterFields.includes("areasOfExpertise")
    );

    return (
      <Grid container spacing={1} alignItems="flex-end" style={{ flex: "0 0 auto" }}>
        <Grid item xs={12}>
          <Input
            autoFocus
            name="text"
            fullWidth
            placeholder={locale.Search}
            onChange={(e) => {
              this.setFilterText(e.target.value);
            }}
            value={this.store.filter.text}
          />
        </Grid>

        <Grid item xs={6}>
          {aoe}
        </Grid>
      </Grid>
    );
  };
}

const styles = (theme: ThemeType) => ({
  inputRoot: {
    cursor: "pointer"
  }
});

export default withStyles(styles)(ConsultantSelector);
